<template>
  <div class="min-h-full flex flex-col">
    <div>
      <div class="flex justify-between items-center mb-10">
        <h4 class="text-3xl sm:text-4xl font-bold leading-tight mt-1">
          Select Your Booking Option
        </h4>
        <!-- Step counter -->
        <StepsCounter class="ml-8 mb-14" v-if="steps.length" :stepCount="steps.length" :currentStep="currentStep"/>
      </div>
      <div class="mt-6 sm:mt-10 text-gray-500 font-light text-xl">
        What type of Booking do you want?
      </div>
    </div>

    <div class="mt-12">
      <ValidationObserver ref="bookingOptionsForm" v-slot="{ handleSubmit }" tag="div">
        <form method="post" @submit.prevent="handleSubmit(saveOption)">
          <div class="space-y-6">
            <template v-for="(option, index) in guestOptionPrices">
              <component
                :is="`${option.slug}-option`"
                :option="option"
                :key="`booking-option-${index}`"
                v-model="selectedOptionId"
                @change="updateSelected"
              />
            </template>
          </div>
          <div class="mt-12 flex justify-center">
            <button
              type="submit"
              class="rounded-full h-16 sm:h-20 bg-indigo-gradiant text-white text-xl font-bold flex justify-center items-center uppercase px-16 disabled:opacity-50"
              :disabled="loading || ! selectedBookingOption"
              :class="{'opacity-50': loading || ! selectedBookingOption}"
            >
              <span v-if="loading">
                <loading-icon class="h-5 w-5"/>
              </span>
              <span v-else>Continue</span>
            </button>
          </div>
          <div v-if="currentStep > 1" class="text-center mt-4">
            <button @click.prevent="prevStep" type="button" class="text-sm text-pink-500 hover:underline">Previous step</button>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
  import { chain } from 'lodash'
  import StepsCounter from '@/components/StepsCounter'
  import VirtualInterviewOption from '@/components/Book/guest/booking-options/VirtualInterviewOption'
  import MiniviewOption from '@/components/Book/guest/booking-options/MiniviewOption'
  import LivePlatformOption from '@/components/Book/guest/booking-options/LivePlatformOption'
  import MediaFeatureOption from '@/components/Book/guest/booking-options/MediaFeatureOption'
  import VirtualEventOption from '@/components/Book/guest/booking-options/VirtualEventOption'

  export default {
    components: {
      StepsCounter,
      VirtualInterviewOption,
      MiniviewOption,
      LivePlatformOption,
      MediaFeatureOption,
      VirtualEventOption,
    },

    data() {
      return {
        loading: false,
        selectedOptionId: null,
        bookingFlowPreferences: {},
      }
    },

    computed: {
      steps() {
        return this.$store.getters['guestBooking/enabledSteps']
      },

      currentStep() {
        return this.$store.state.guestBooking.currentStep
      },

      questions() {
        return this.$store.state.guestBooking.questions
      },

      user() {
        return this.$store.getters['auth/getUser']
      },

      show() {
        return this.$store.getters['guestBooking/show']
      },

      guest() {
        return this.$store.getters['guest/getGuest']
      },

      bookingOptions() {
        return this.$store.getters['guestBooking/bookingOptions']
      },

      guestOptionPrices() {
        return chain(this.bookingOptions)
          .filter(option => option.slug != 'pitch')
          .map(option => {
            return {
              ...option,
              prices: this.guest.option_prices.filter(price => price.booking_option_id === option.id)
            }
          })
          .filter(option => option.prices.length)
          .sortBy(option => option.id)
          .value()
      },

      selectedBookingOption() {
        return this.$store.getters['guestBooking/selectedBookingOption']
      },

      isPitch() {
        return this.$store.getters['guestBooking/isPitch']
      },
    },

    watch: {
      selectedBookingOption(selectedBookingOption) {
        if (selectedBookingOption.slug === 'miniview') {
          this.$store.commit('guestBooking/enableStep', 'miniview-questions')
        } else {
          this.$store.commit('guestBooking/disableStep', 'miniview-questions')
        }
      }
    },

    methods: {
      updateSelected(event) {
        this.selectedOptionId = event.option

        let option = this.guestOptionPrices.find(option => option.id == event.option)

        this.$store.commit('guestBooking/setSelectedBookingOption', {
          ...option,
          price: event.price,
          amount: event.amount || 1,
        })
      },

      saveOption() {
        this.nextStep()
      },

      determineStepNumber() {
        let step = this.steps.findIndex(step => step.name == 'options')

        if (step === -1) {
          return this.$router.push({ name: this.steps[0].route })
        }

        this.$store.commit('guestBooking/setCurrentStep', step + 1)
      },

      nextStep() {
        let nextStepRoute = this.steps[this.currentStep].route

        this.$router.push({ name: nextStepRoute })
      },

      prevStep() {
        let prevStepRoute = this.steps[this.currentStep - 2].route
        this.$store.commit('guestBooking/setSelectedBookingOption', null)

        this.$router.push({ name: prevStepRoute })
      }
    },

    mounted() {
      this.determineStepNumber()

      if (! this.show || ! this.show.id) {
        this.$router.push({name: this.steps[0].route})
        return
      }

      if (this.isPitch) {
        const step = this.steps.find(step => step.name === 'pitch');

        if (step) {
          this.$router.push({name: step.route});
        }
      }

      if (this.selectedBookingOption) {
        this.selectedOptionId = this.selectedBookingOption.id
      }

      this.$store.dispatch('guest/fetchBookingFlowPreferences', this.guest.id).then((data) => {
        this.bookingFlowPreferences = data.data.data
      })

      // this.$store.commit('guestBooking/setIsPitch', false);
    }
  }
</script>
